import { Css, FilterDefs, Filters, multiFilter } from "@homebound/beam";
import { useEffect, useMemo, useState } from "react";
import { SearchBox } from "src/components";
import { MaterialVariantsForPlaceholderTliFilter } from "src/generated/graphqlTypes";
import {
  PlaceholderProductSearch_RequirementsItemFragment,
  PlaceholderProductSearch_RequirementsMaterialFragment,
} from "src/routes/libraries/design-catalog/components/PlaceholderProductSearch.generated";
import { stripEmptyString } from "src/utils";

type ProductSearchFiltersProps = {
  placeholderTliId: string;
  setFilter: (filter: MaterialVariantsForPlaceholderTliFilter) => void;
  placeholderMv: PlaceholderProductSearch_RequirementsMaterialFragment;
  placeholderItem: PlaceholderProductSearch_RequirementsItemFragment;
};

// Our backend filter isn't the same as what Beam wants (the backend wants a single `mavIds` array with
// all dimensions included in it (`mav:width32, mav:colorBlue`), while the UI / Beam FilterDef wants
// per-dimension filters/dropdowns (`Width: ...`, `Color: ...)`, so use this as the go between.
//
// We use a `Record<string, ...> ` instead of hardcoded keys b/c the "filters" that Beam sees (Width, Color, etc.)
// are dynamic based on the Item we're searching for.
type GenericFilter = Record<string, string[]>;

/** Adapts the "filters for a material search" (brand & mav-based) to Beam's Filters. */
export function ProductSearchFilters(props: ProductSearchFiltersProps) {
  const { placeholderMv, placeholderItem, placeholderTliId, setFilter: setParentFilter } = props;

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<GenericFilter>({});
  const filterDefs: FilterDefs<GenericFilter> = useMemo(() => {
    const brands = placeholderItem.materialBrands.sortByKey("name");
    const requirementMadIds = placeholderMv.materialAttributeValues.map((mav) => mav.dimension.id);
    return {
      // Create a `Brand: ...` filter
      ...(brands.nonEmpty && {
        brandIds: multiFilter({
          label: "Brand",
          options: brands,
          getOptionValue: (o) => o.id,
          getOptionLabel: (o) => o.name,
        }),
      }),
      // Create a variable number of `[MAD]: ...` filters, keyed by `mad:123`
      ...placeholderItem.materialAttributeDimensions
        .filter((mad) => mad.values?.nonEmpty)
        // Filter IN any MADs which are NOT already present on the Placeholder TLi's MV. Only allow supplemental MAVs.
        .filter((mad) => !requirementMadIds.includes(mad.id))
        .map((mad) => {
          return [
            mad.id,
            multiFilter({
              label: mad.name,
              options: mad.values?.map((mav) => ({ id: mav.id, name: mav.textValue })) ?? [],
              getOptionValue: (o) => o.id,
              getOptionLabel: (o) => o.name,
            }),
          ] as const;
        })
        .toObject(),
    };
  }, [placeholderMv, placeholderItem]);

  // Combines our internal search + filter and calls the external setFilter w/them glued together
  useEffect(() => {
    const brandIds = "brandIds" in filter && filter.brandIds.nonEmpty ? filter.brandIds : undefined;
    const attributeIds = Object.entries(filter)
      .filter(([key]) => key !== "brandIds")
      .flatMap(([, ids]) => ids);
    setParentFilter({
      takeoffLineItemId: placeholderTliId,
      brandIds: brandIds?.nonEmpty ? brandIds : undefined,
      attributeIds: attributeIds.nonEmpty ? attributeIds : undefined,
      search: stripEmptyString(search),
    });
  }, [placeholderTliId, filter, search, setParentFilter]);

  return (
    <div css={Css.df.jcsb.my2.$}>
      <Filters<GenericFilter> filterDefs={filterDefs} filter={filter} onChange={setFilter} />
      <SearchBox
        onSearch={setSearch}
        clearable
        debounceDelayInMs={150}
        updateQueryString={false}
        placeholder="Search within results..."
      />
    </div>
  );
}
